import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

/**
 * When embedded in Volugraph, locale JSON must load from /supersplat/static/locales/…
 * (set by SuperSplatMountHost before the module runs). A relative ./static/locales path
 * resolves against the SPA route (e.g. /studio/.../static/locales) and 404s, so UI shows raw i18n keys.
 */
const resolveLocaleLoadPath = (languages: string | string[], _namespaces?: string | string[]) => {
    const lngs = Array.isArray(languages) ? languages : [languages];
    const lng = lngs[0] || 'en';
    if (typeof window !== 'undefined') {
        const base = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_BASE__?: string }).__VOLUGRAPH_SUPER_SPLAT_BASE__;
        if (base) {
            return `${base.replace(/\/+$/, '/')}static/locales/${lng}.json`;
        }
    }
    return `./static/locales/${lng}.json`;
};

const localizeInit = () => {
    return i18next
    .use(Backend)
    .use(LanguageDetector)
    .init({
        showSupportNotice: false,
        detection: {
            order: ['querystring', /* 'cookie', 'localStorage', 'sessionStorage',*/ 'navigator', 'htmlTag']
        },
        backend: {
            loadPath: resolveLocaleLoadPath
        },
        supportedLngs: ['de', 'en', 'es', 'fr', 'ja', 'ko', 'pt-BR', 'ru', 'zh-CN'],
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false
        }
    });
};

interface LocalizeOptions {
    ellipsis?: boolean;
}

const FALLBACK_TEXT: Record<string, string> = {
    'tooltip.bottom-toolbar.undo': 'Undo',
    'tooltip.bottom-toolbar.redo': 'Redo',
    'tooltip.bottom-toolbar.rect': 'Rect Select',
    'tooltip.bottom-toolbar.lasso': 'Lasso Select',
    'tooltip.bottom-toolbar.brush': 'Brush Select',
    'tooltip.bottom-toolbar.sphere': 'Sphere Select',
    'tooltip.bottom-toolbar.box': 'Box Select',
    'tooltip.bottom-toolbar.translate': 'Translate',
    'tooltip.bottom-toolbar.rotate': 'Rotate',
    'tooltip.bottom-toolbar.scale': 'Scale',
    'tooltip.bottom-toolbar.measure': 'Measurement',
    'menu.select.delete': 'Delete',
    'menu.select.invert': 'Invert',
    'measure.length': 'Length',
    'toolbar.select': 'Select',
    'toolbar.invert': 'Invert',
    'toolbar.radius': 'Radius',
    'panel.render.exporting-video': 'EXPORTING VIDEO',
    'panel.render.cancel': 'CANCEL',
    'timeline.length.label': 'LENGTH',
    'tooltip.timeline.length': 'Timeline length in frames',
};

const localize = (key: string, options?: LocalizeOptions): string => {
    let text = i18next.t(key);
    if (text === key && FALLBACK_TEXT[key]) {
        text = FALLBACK_TEXT[key];
    }

    if (options?.ellipsis) text += '...';

    return text;
};

const getLocale = (): string => {
    return i18next.language || 'en';
};

const formatInteger = (value: number): string => {
    return new Intl.NumberFormat(getLocale(), {
        maximumFractionDigits: 0
    }).format(Math.round(value));
};

export { localizeInit, localize, formatInteger };
export type { LocalizeOptions };
